import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { InsuranceTabStore, InsuranceTabUtils } from '@frontend/insurance-tab';
import { theme } from '@frontend/theme';
import { Button, Text, PopoverDialog, usePopoverDialog, useFormField, RadioField, Dot } from '@frontend/design-system';
import { getUniqueInsuranceStatuses } from '../../utils';
import { ResetButton } from './ResetButton';
import { ScheduleInsuranceTabTrackingIds } from './trackingIds';

type FilterOptionType = {
  label: string;
  value: string;
};

type FilterButtonProps = {
  isIconOnly?: boolean;
};

export const FilterButton = ({ isIconOnly }: FilterButtonProps) => {
  const { t } = useTranslation('scheduleCalendarActions');

  const { currentFilterValue, setCurrentFilterValue, setCurrentStatusSelection } =
    InsuranceTabStore.useInsuranceTabShallowStore(
      'currentFilterValue',
      'setCurrentFilterValue',
      'setCurrentStatusSelection'
    );

  const filterPopOver = usePopoverDialog();

  const filterSelectionRadioFieldProps = useFormField({ type: 'radio', value: currentFilterValue }, [
    currentFilterValue,
  ]);

  const verificationStatusOptions = useMemo(() => {
    const filterOptions: FilterOptionType[] = [];
    const uniqueOptions = getUniqueInsuranceStatuses();
    Array.from(uniqueOptions.entries()).forEach((status) => {
      const [_, value] = status;
      if (value.text) {
        filterOptions.push({
          label: InsuranceTabUtils.toTitleCase(value.text),
          value: value.text.toLowerCase(),
        });
      }
    });
    return Array.from(filterOptions).sort();
  }, []);

  const handleFilterSelection = (filterValue: string) => {
    setCurrentFilterValue(filterValue);
    setCurrentStatusSelection('');
    filterPopOver.close();
  };

  return (
    <>
      <section css={{ position: 'relative' }}>
        <Button
          trackingId={ScheduleInsuranceTabTrackingIds.filterButton}
          size='small'
          variant='secondary'
          iconName='filter-alt'
          css={{ backgroundColor: theme.colors.white, borderColor: theme.colors.neutral30 }}
          {...filterPopOver.getTriggerProps()}
          disabled={!verificationStatusOptions?.length}
        >
          {isIconOnly ? '' : t('Filter')}
          {currentFilterValue && <Dot css={dotStyles} color='primary' />}
        </Button>
      </section>

      <PopoverDialog {...filterPopOver.getDialogProps()}>
        <Text size='large' weight='bold' css={{ marginBottom: theme.spacing(2) }}>
          {t('Verification Status')}
        </Text>
        <RadioField {...filterSelectionRadioFieldProps} name='insurance-tab-filters'>
          {verificationStatusOptions.map((options) => (
            <RadioField.Radio key={options.value} value={options.value}>
              {options.label}
            </RadioField.Radio>
          ))}
        </RadioField>
        <div css={{ marginTop: theme.spacing(2), display: 'flex', alignItems: 'center' }}>
          <Button
            variant='primary'
            size='large'
            css={{ width: '100%' }}
            onClick={() => handleFilterSelection(filterSelectionRadioFieldProps.value)}
          >
            {t('Apply')}
          </Button>
          <ResetButton />
        </div>
      </PopoverDialog>
    </>
  );
};

const dotStyles = css({
  display: 'absolute',
  top: -3,
  right: -6,
});
